import React, { createContext, useCallback, useContext, useState } from "react";
import type { Toast, ToastVariant } from "@/types/ui";

interface AddToastProps {
  variant?: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  addToast: (t: AddToastProps) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

export default function ToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const addToast = useCallback(
    (t: AddToastProps) => {
      const id = `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const toast: Toast = {
        id,
        variant: t.variant ?? "info",
        title: t.title,
        duration: t.duration ?? 4000,
        ...(t.description ? { description: t.description } : {}),
      };
      setToasts((prev) => [...prev, toast]);

      // auto remove
      window.setTimeout(() => removeToast(id), toast.duration);
      return id;
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}

      {/* Toast container */}
      <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex max-w-xs flex-col gap-3">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto w-full rounded-md border px-3 py-2 shadow-lg transition-opacity ${
              t.variant === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : t.variant === "error"
                  ? "border-red-200 bg-red-50 text-red-800"
                  : t.variant === "warning"
                    ? "border-yellow-200 bg-yellow-50 text-yellow-800"
                    : "border-neutral-200 bg-neutral-50 text-neutral-900"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">{t.title}</div>
                {t.description && (
                  <div className="mt-1 text-xs opacity-80">{t.description}</div>
                )}
              </div>
              <button
                type="button"
                aria-label="Dismiss toast"
                onClick={() => removeToast(t.id)}
                className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded bg-white/0 text-sm text-neutral-500 hover:text-neutral-700"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
